/* ==========================================================================
   Animation Manager - Detects Windows Settings & Uses JS Fallback
   Asian American Forward Website
   ========================================================================== */

import { 
  animate, 
  animateEntrance, 
  animateExit, 
  animateHover, 
  resetHover, 
  animatePulse, 
  animateRainbowBorder, 
  animateSpin,
  areWindowsAnimationsDisabled 
} from './js-animations.js';

// Global state
let useJavaScriptAnimations = false;
let isInitialized = false;

/* ==========================================================================
   Initialization and Detection
   ========================================================================== */

export function initializeAnimationManager() {
  if (isInitialized) return;
  
  console.log('🎬 Initializing Animation Manager...');
  
  // Check if Windows animations are disabled
  useJavaScriptAnimations = areWindowsAnimationsDisabled();
  
  if (useJavaScriptAnimations) {
    console.log('⚠️ Windows animations disabled - Using JavaScript animations as fallback');
    disableCSSAnimations();
    setupJavaScriptAnimations();
  } else {
    console.log('✅ CSS animations working normally');
    // Still setup navigation animations to ensure they work regardless of Windows settings
    setupNavigationAnimations();
  }
  
  isInitialized = true;
  
  // Listen for system preference changes
  setupSystemPreferenceListeners();
}

function disableCSSAnimations() {
  // Inject CSS to disable all CSS animations and transitions
  const style = document.createElement('style');
  style.id = 'disable-css-animations';
  style.textContent = `
    /* Disable all CSS animations and transitions when using JS fallback */
    *, *::before, *::after {
      animation-duration: 0s !important;
      animation-delay: 0s !important;
      transition-duration: 0s !important;
      transition-delay: 0s !important;
    }
    
    /* Navigation elements use JavaScript animations when CSS is disabled */
    .nav-menu {
      transition: none !important;
    }
    
    .nav-toggle-line {
      transition: none !important;
    }
    
    /* JavaScript navigation animation support */
    body.js-nav-animations .nav-menu {
      transition: none !important;
    }
    
    body.js-nav-animations .nav-toggle-line {
      transition: none !important;
    }
    
    /* Remove transform transitions that might interfere */
    .hero-card,
    .event-card,
    .about-img,
    .mission-img,
    .hover-lift,
    .hover-scale {
      transition: none !important;
    }
  `;
  document.head.appendChild(style);
}

function setupSystemPreferenceListeners() {
  // Listen for changes in animation preferences
  const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  
  reducedMotionQuery.addEventListener('change', (e) => {
    if (e.matches) {
      console.log('🔇 Reduced motion preference detected');
      // Disable all animations including JavaScript ones
      useJavaScriptAnimations = false;
    } else {
      // Re-check Windows animation status
      useJavaScriptAnimations = areWindowsAnimationsDisabled();
      if (useJavaScriptAnimations) {
        setupJavaScriptAnimations();
      }
    }
  });
}

/* ==========================================================================
   JavaScript Animation Setup
   ========================================================================== */

function setupJavaScriptAnimations() {
  // Wait for DOM to be ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      setupAllAnimations();
    });
  } else {
    setupAllAnimations();
  }
}

function setupAllAnimations() {
  setupHeroAnimations();
  setupCardAnimations();
  setupNavigationAnimations();
  setupButtonAnimations();
  setupHoverEffects();
  setupLoadingAnimations();
}

/* ==========================================================================
   Hero Section Animations
   ========================================================================== */

function setupHeroAnimations() {
  // Hero text entrance animations
  const heroLines = document.querySelectorAll('.hero-line');
  heroLines.forEach((line, index) => {
    animateEntrance(line, 'left', {
      duration: 800,
      delay: (index + 1) * 200,
      distance: 30
    });
  });
  
  // Hero description
  const heroDescription = document.querySelector('.hero-description');
  if (heroDescription) {
    animateEntrance(heroDescription, 'up', {
      duration: 800,
      delay: 800,
      distance: 20
    });
  }
  
  // Hero actions
  const heroActions = document.querySelector('.hero-actions');
  if (heroActions) {
    animateEntrance(heroActions, 'up', {
      duration: 800,
      delay: 1000,
      distance: 20
    });
  }
}

/* ==========================================================================
   Card Animations
   ========================================================================== */

function setupCardAnimations() {
  // Hero cards entrance
  const heroCards = document.querySelectorAll('.hero-card');
  heroCards.forEach((card, index) => {
    // Set initial state
    card.style.opacity = '0';
    card.style.transform = 'translateY(50px) scale(0.8)';
    
    animateEntrance(card, 'up', {
      duration: 600,
      delay: 800 + (index * 200),
      distance: 50
    }).then(() => {
      // Set final opacity based on card index
      const cardIndex = parseInt(card.getAttribute('data-card-index')) || 0;
      let targetOpacity = 1;
      
      switch (cardIndex) {
        case 1: targetOpacity = 0.9; break;
        case 2: targetOpacity = 0.8; break;
        case 3: targetOpacity = 0.7; break;
      }
      
      if (targetOpacity !== 1) {
        animate(card, { opacity: targetOpacity }, { duration: 200 });
      }
    });
  });
  
  // Event cards
  const eventCards = document.querySelectorAll('.modal-event-card');
  eventCards.forEach(card => {
    // Hover animations - no scaling, just lift
    card.addEventListener('mouseenter', () => {
      if (prefersReducedMotion) return;
      
      card.style.transition = 'transform 0.3s ease, box-shadow 0.3s ease';
      card.style.transform = 'translateY(-8px)';
      card.style.boxShadow = '0 12px 48px rgba(0, 0, 0, 0.12), 0 6px 24px rgba(0, 0, 0, 0.08)';
    });
    
    card.addEventListener('mouseleave', () => {
      card.style.transform = 'translateY(0)';
      card.style.boxShadow = '0 4px 24px rgba(0, 0, 0, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04)';
    });
  });
}

/* ==========================================================================
   Navigation Animations
   ========================================================================== */

function setupNavigationAnimations() {
  // Setup JavaScript navigation animations for Windows animation compatibility
  console.log('📱 Setting up JavaScript navigation animations for Windows compatibility');
  
  const navToggle = document.querySelector('.nav-toggle');
  const navMenu = document.querySelector('.nav-menu');
  const navToggleLines = document.querySelectorAll('.nav-toggle-line');
  
  if (!navToggle || !navMenu || !navToggleLines.length) {
    console.warn('Navigation elements not found for JavaScript animation');
    return;
  }
  
  // Add body class to indicate JavaScript animations are active
  document.body.classList.add('js-nav-animations');
  
  // Ensure menu starts in closed state
  navMenu.style.transform = 'translateY(-100%)';
  navMenu.style.opacity = '0';
  navMenu.style.visibility = 'hidden';
  
  // Override the existing click handler with JavaScript animations
  navToggle.addEventListener('click', function(e) {
    e.preventDefault();
    e.stopImmediatePropagation(); // Prevent other handlers
    
    const isExpanded = navToggle.getAttribute('aria-expanded') === 'true';
    const newState = !isExpanded;
    
    // Update aria state
    navToggle.setAttribute('aria-expanded', newState);
    
    if (newState) {
      // Opening: Show menu first, then animate hamburger to X
      navMenu.classList.add('expanded');
      animateMenuOpen(navMenu);
      animateHamburgerToX(navToggleLines);
    } else {
      // Closing: Animate X to hamburger, then hide menu
      animateXToHamburger(navToggleLines);
      animateMenuClose(navMenu).then(() => {
        navMenu.classList.remove('expanded');
      });
    }
  }, true); // Use capture to ensure this runs first
}

function animateMenuOpen(menu) {
  // Force initial state
  menu.style.transform = 'translateY(-100%)';
  menu.style.opacity = '0';
  menu.style.visibility = 'visible';
  
  // Animate to visible state
  animate(menu, {
    translateY: 0,
    opacity: 1
  }, { 
    duration: 350, 
    easing: 'ease-out' 
  });
}

function animateMenuClose(menu) {
  return animate(menu, {
    translateY: -100,
    opacity: 0
  }, { 
    duration: 350, 
    easing: 'ease-in' 
  }).then(() => {
    menu.style.visibility = 'hidden';
  });
}

function animateHamburgerToX(lines) {
  if (lines.length >= 3) {
    // Top line: move down and rotate
    animate(lines[0], {
      translateY: 6,
      rotate: 45
    }, { duration: 300, easing: 'ease-out' });
    
    // Middle line: fade out and scale down
    animate(lines[1], {
      opacity: 0,
      scale: 0
    }, { duration: 300, easing: 'ease-out' });
    
    // Bottom line: move up and rotate
    animate(lines[2], {
      translateY: -6,
      rotate: -45
    }, { duration: 300, easing: 'ease-out' });
  }
}

function animateXToHamburger(lines) {
  if (lines.length >= 3) {
    // Reset all lines to hamburger state
    animate(lines[0], {
      translateY: 0,
      rotate: 0
    }, { duration: 300, easing: 'ease-out' });
    
    animate(lines[1], {
      opacity: 1,
      scale: 1
    }, { duration: 300, easing: 'ease-out' });
    
    animate(lines[2], {
      translateY: 0,
      rotate: 0
    }, { duration: 300, easing: 'ease-out' });
  }
}

/* ==========================================================================
   Button Animations
   ========================================================================== */

function setupButtonAnimations() {
  // Rainbow border animation disabled - using simple black border instead
  const donateButton = document.querySelector('.nav-link--primary');
  if (donateButton) {
    console.log('🚫 Donate button rainbow animation disabled - using simple black border');
    
    // Ensure simple black border
    donateButton.style.borderColor = '#000000';
    donateButton.style.removeProperty('box-shadow');
  } else {
    console.warn('❌ Donate button not found - JavaScript animation cannot be initialized');
  }
  
  // Loading spinner
  const loadingSpinners = document.querySelectorAll('.loading-spinner');
  loadingSpinners.forEach(spinner => {
    animateSpin(spinner, { duration: 1000, continuous: true });
  });
}

/* ==========================================================================
   Hover Effects
   ========================================================================== */

function setupHoverEffects() {
  // Generic hover lift effects
  const hoverLiftElements = document.querySelectorAll('.hover-lift');
  hoverLiftElements.forEach(element => {
    element.addEventListener('mouseenter', () => {
      animateHover(element, {
        translateY: -4
      }, { duration: 200 });
    });
    
    element.addEventListener('mouseleave', () => {
      animate(element, {
        translateY: 0
      }, { duration: 200 });
    });
  });
  
  // Generic hover scale effects
  const hoverScaleElements = document.querySelectorAll('.hover-scale');
  hoverScaleElements.forEach(element => {
    element.addEventListener('mouseenter', () => {
      animateHover(element, {
        scale: 1.05
      }, { duration: 200 });
    });
    
    element.addEventListener('mouseleave', () => {
      animate(element, {
        scale: 1
      }, { duration: 200 });
    });
  });
  
  // Image hover effects
  const aboutImg = document.querySelector('.about-img');
  if (aboutImg) {
    aboutImg.addEventListener('mouseenter', () => {
      animateHover(aboutImg, {
        translateY: -4,
        scale: 1.02
      }, { duration: 300 });
    });
    
    aboutImg.addEventListener('mouseleave', () => {
      animate(aboutImg, {
        translateY: 0,
        scale: 1
      }, { duration: 300 });
    });
  }
  
  const missionImg = document.querySelector('.mission-img');
  if (missionImg) {
    missionImg.addEventListener('mouseenter', () => {
      animateHover(missionImg, {
        translateY: -4,
        scale: 1.02
      }, { duration: 300 });
    });
    
    missionImg.addEventListener('mouseleave', () => {
      animate(missionImg, {
        translateY: 0,
        scale: 1
      }, { duration: 300 });
    });
  }
}

/* ==========================================================================
   Loading Animations
   ========================================================================== */

function setupLoadingAnimations() {
  // Loading screen animations (if not already handled)
  const loadingLines = document.querySelectorAll('.loading-line');
  loadingLines.forEach((line, index) => {
    animateEntrance(line, 'right', {
      duration: 800,
      delay: 200 + (index * 300),
      distance: 50
    });
  });
  
  // Pulse animations
  const pulseElements = document.querySelectorAll('.animate-pulse, .notification-icon');
  pulseElements.forEach(element => {
    animatePulse(element, { 
      duration: 2000, 
      minOpacity: 0.7, 
      maxOpacity: 1 
    });
  });
}

/* ==========================================================================
   Utility Functions
   ========================================================================== */

export function animateElementEntrance(element, options = {}) {
  if (!useJavaScriptAnimations && !areWindowsAnimationsDisabled()) {
    // Use CSS animations if they're working
    element.classList.add('animate-fade-in');
    return Promise.resolve();
  }
  
  return animateEntrance(element, options.direction || 'up', options);
}

export function animateElementExit(element, options = {}) {
  if (!useJavaScriptAnimations && !areWindowsAnimationsDisabled()) {
    // Use CSS animations if they're working
    element.classList.add('animate-fade-out');
    return Promise.resolve();
  }
  
  return animateExit(element, options.direction || 'up', options);
}

export function isUsingJavaScriptAnimations() {
  return useJavaScriptAnimations;
}

/* ==========================================================================
   Public API
   ========================================================================== */

export { animate, animateHover, resetHover, animatePulse, animateRainbowBorder, animateSpin };

console.log('🎯 Animation Manager Loaded - Smart CSS/JS Animation Detection'); 